import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
} from '@shared/schemas';

import type {
  DeactivateStatus,
  EnrichStatus,
  LoadStatus,
  NotesSaveStatus,
  ReactivateStatus,
} from '../domain/profile-detail.model';
import type { TypeCategories } from '../domain/profile-detail.rules';

/** Enrichment lifecycle (`error` holds the failure snackbar message). */
export interface EnrichState {
  status: EnrichStatus;
  error: string | null;
}

/** Deactivation lifecycle (`error` holds the failure snackbar message). */
export interface DeactivateState {
  status: DeactivateStatus;
  error: string | null;
}

/**
 * Reactivation lifecycle (#659). `immunised` is true once a scan-deactivated
 * profile is restored: future keyword scans will leave it alone.
 */
export interface ReactivateState {
  status: ReactivateStatus;
  error: string | null;
  immunised: boolean;
}

/**
 * The admin contact-verification toggle (#556).
 *
 * `pending` holds WHICH contact is in flight, not just a boolean: the panel
 * shows two toggles, and a shared flag would grey out both while one is saving.
 * `error` holds the failure snackbar message.
 */
export interface ContactStatusState {
  pending: 'email' | 'phone' | null;
  error: string | null;
}

/**
 * Network tab rows. `loaded` is distinct from `!loading`: the tab's count badge
 * must stay blank until the first load settles, and only then show a real count
 * (including 0). A failed load also lands `loaded: true` with no rows.
 */
export interface CommonNetworkState {
  rows: CommonNetworkProfile[];
  loading: boolean;
  loaded: boolean;
}

/**
 * The admin's "Add to list" dialog (#614): the lists it can pick from, and the
 * POST. `added` holds the list's name once the POST succeeds, for the snackbar;
 * the reload carries the new membership, so there is nothing else to hold.
 */
export interface AddToListState {
  lists: ListResponse[];
  listsStatus: LoadStatus;
  pending: boolean;
  error: string | null;
  added: string | null;
}

/**
 * Redux state for the Profile Detail page. The slice owns the loaded profile, its
 * load lifecycle, the enrichment lifecycle (POST + the "panel is open"
 * acknowledgement) and the common-network tab. Ephemeral UI (active tab, the
 * force-refresh dialog, the live enrichment drawer's open flag) stays local to
 * the page. Every field is serialisable.
 */
/**
 * The admin's "Types" dialog (#652): the profile's types and the ones removed by
 * hand, and which type a Remove / Restore is in flight for.
 */
export interface TypesEditState {
  status: LoadStatus;
  data: ProfileTypesEdit | null;
  pendingType: string | null;
  error: string | null;
}

/**
 * A client's "Types" dialog (#723): the types the caller's licence sees on the
 * profile, which it added, and which type an Add / Remove is in flight for.
 */
export interface LicenceTypesEditState {
  status: LoadStatus;
  data: LicenceProfileTypesEdit | null;
  pendingType: string | null;
  error: string | null;
}

/**
 * The Notes tab (#676): the caller's licence's notes on the profile, their load
 * lifecycle, and the save's. `profileId` says whose notes these are, so a
 * response still in flight when the reader moved to another profile is dropped
 * by the reducer instead of being shown there.
 */
export interface NotesState {
  profileId: string | null;
  notes: ProfileNotes | null;
  status: LoadStatus;
  /** Why the load failed; the save's own error is on `save`. */
  error: string | null;
  save: { status: NotesSaveStatus; error: string | null };
}

export interface ProfileDetailState {
  profile: ProfileResponse | null;
  loadStatus: LoadStatus;
  enrich: EnrichState;
  deactivate: DeactivateState;
  reactivate: ReactivateState;
  contactStatus: ContactStatusState;
  commonNetwork: CommonNetworkState;
  addToList: AddToListState;
  /** Admin only (#629): each type's main category, null until loaded or when it failed. */
  typeCategories: TypeCategories | null;
  typesEdit: TypesEditState;
  licenceTypesEdit: LicenceTypesEditState;
  notes: NotesState;
}

export const emptyEnrich: EnrichState = { status: 'idle', error: null };

export const emptyDeactivate: DeactivateState = { status: 'idle', error: null };

export const emptyReactivate: ReactivateState = { status: 'idle', error: null, immunised: false };

export const emptyTypesEdit: TypesEditState = { status: 'idle', data: null, pendingType: null, error: null };

export const emptyLicenceTypesEdit: LicenceTypesEditState = { status: 'idle', data: null, pendingType: null, error: null };

export const emptyNotes: NotesState = {
  profileId: null,
  notes: null,
  status: 'idle',
  error: null,
  save: { status: 'idle', error: null },
};

export const emptyContactStatus: ContactStatusState = { pending: null, error: null };

export const emptyCommonNetwork: CommonNetworkState = {
  rows: [],
  loading: false,
  loaded: false,
};

export const emptyAddToList: AddToListState = {
  lists: [],
  listsStatus: 'idle',
  pending: false,
  error: null,
  added: null,
};

export const initialState: ProfileDetailState = {
  profile: null,
  loadStatus: 'idle',
  enrich: emptyEnrich,
  deactivate: emptyDeactivate,
  reactivate: emptyReactivate,
  contactStatus: emptyContactStatus,
  commonNetwork: emptyCommonNetwork,
  addToList: emptyAddToList,
  typeCategories: null,
  typesEdit: emptyTypesEdit,
  licenceTypesEdit: emptyLicenceTypesEdit,
  notes: emptyNotes,
};
